import React, { useMemo, useState } from 'react';
import ChatMediaModal from './ChatMediaModal';
import { ChatMessage } from './interfaces';
import { decodeMediaInChat } from 'utilities/functions';
import { ReactComponent as File } from 'assets/icons/file.svg';
import { formatFileType } from '../../../utilities/fileHelpers';
import { ContentType } from '../../../graphql/generated/hasura';
import ButtonComponent from '../../../components/button/buttonComponent';
import { useGetFileFromStorage } from '../../../hooks/useGetFileFromStorage';
import { ChatMediaImageThumb } from './ChatMediaImageThumb';
import Loader from '../../../components/loaderComponent';

const IMG_FILES_TYPE = [
  ContentType.Jpg,
  ContentType.Jpeg,
  ContentType.Png,
  ContentType.Gif,
];

const isImageByExt = (ext?: ContentType | null) => {
  if (!ext) return false;
  return IMG_FILES_TYPE.includes(ext);
};

const collectMediaMessages = (messages: ChatMessage[]) => {
  const result: ChatMessage[] = [];

  for (const m of messages || []) {
    if (m.messageGroup?.length) {
      for (const gm of m.messageGroup) {
        const decoded = decodeMediaInChat(gm.message as string);
        if (decoded?.mediaUrl) result.push(gm);
      }
    }

    const decodedSelf = decodeMediaInChat(m.message as string);
    if (decodedSelf?.mediaUrl) result.push(m);
  }

  return result;
};

export const MediaChatComponent: React.FC<{
  messages: ChatMessage[];
  appointmentId: string;
  loading?: boolean;
}> = ({ messages, appointmentId, loading }) => {
  const [src, setSrc] = useState<string | null | undefined>(null);

  const mediaItems = useMemo(() => collectMediaMessages(messages), [messages]);

  const { getUrlFileFromVonageStorage } = useGetFileFromStorage();

  const openOrDownload = async (
    id: string,
    appointmentId: string,
    fileName: string,
    ext?: string | null,
    fileUrl?: string,
  ) => {
    try {
      const url = await getUrlFileFromVonageStorage(id, appointmentId);
      if (!url) throw new Error('File not found');

      const safeExt = (ext ?? '').toLowerCase();

      if (safeExt === 'pdf') {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }

      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) throw new Error(`Failed to fetch file: ${res.status}`);

      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = safeExt ? `${fileName}.${safeExt}` : fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      if (fileUrl) {
        window.open(fileUrl, '_blank', 'noopener,noreferrer');
      }
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="flex flex-col pr-2 gap-4 max-w-full h-full overflow-y-auto max-h-[1500px] w-full">
      {!mediaItems.length ? (
        <div className="text-med-gray-3 w-full text-center">
          No attached files
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {mediaItems.map((message, idx) => {
            const decoded = decodeMediaInChat(message.message as string);
            if (!decoded?.mediaUrl) return null;

            const url = decoded.mediaUrl;
            const ext = formatFileType(
              decoded.contentType as string,
            ) as ContentType;
            const isImage = isImageByExt(ext);

            const key = message.id || `${decoded.mediaId}-${idx}`;

            if (isImage) {
              return (
                <ChatMediaImageThumb
                  key={key}
                  url={url}
                  mediaId={decoded.mediaId}
                  appointmentId={appointmentId}
                  onClick={(resolved) => setSrc(resolved)}
                />
              );
            }

            return (
              <div
                key={key}
                className="bg-base-content flex flex-col gap-2.5 justify-center w-full aspect-square rounded-[10px]  p-3 overflow-hidden"
              >
                <div className="flex items-center flex-col gap-2 min-w-0">
                  <File className="w-5 h-5 min-w-[20px] fill-med-gray-3 text-med-gray-3" />
                  <div className="min-w-0 flex items-center">
                    <div className="text-xs truncate max-w-[100px]">
                      {decoded.fileName}
                    </div>
                    {ext && <div className="text-xs lowercase">.{ext}</div>}
                  </div>
                </div>

                <ButtonComponent
                  testID={`download-${key}`}
                  type="underline"
                  onClick={() =>
                    openOrDownload(
                      decoded?.mediaId,
                      appointmentId,
                      decoded.fileName,
                      ext,
                      url,
                    )
                  }
                >
                  VIEW
                </ButtonComponent>
              </div>
            );
          })}
        </div>
      )}

      <ChatMediaModal src={src} setSrc={setSrc} />
    </div>
  );
};
